/**
 * Sprint 23.6 — InlinePersonaEdit.
 *
 * The post-hire persona editor primitive. Wraps any single persona field on the
 * live agent profile with the inline-edit pattern (Q15):
 *   - Hover the row → pencil + sparkle affordances fade in.
 *   - Click pencil → the value swaps for a textarea / single-line input /
 *     <select> / tag editor in place (no modal, no separate view).
 *   - Click away (blur) OR Enter for single-line / Cmd-Enter for multiline
 *     commits and saves; Esc cancels.
 *
 * The Sprint 23.5 wizard (`HireDraftField`) showed pencil + sparkle persistently
 * — fine for a focused composition surface. On the live profile we want quieter
 * chrome (Q15 spec note: "Reduces visual noise; reveals on intent"), so the
 * affordances are hover-only here.
 *
 * Editing modes:
 *   - `multiline`        → <textarea>; Cmd/Ctrl-Enter saves.
 *   - default            → single-line <input>; Enter saves.
 *   - `options`          → <select>; instant save on change.
 *   - `tags`             → comma-separated string[] editor; Enter adds tag.
 *
 * Save is delegated. The parent (AgentSettingsTab / AgentProfileHeader) wires
 * the actual `update_node({agent_config: {...patch}})` mutation; that keeps
 * this primitive ignorant of agent shape and reusable for any field.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

export type InlinePersonaMode = "text" | "textarea" | "select" | "tags";

export interface InlinePersonaOption {
  value: string;
  label: string;
}

export interface InlinePersonaEditProps {
  /** Field label shown above the value. Title-case; theming is the caller's job. */
  label: string;
  /**
   * Suppress the visible label row text (the hero bio shows only the paragraph;
   * the Settings field-grid supplies its own left-column label). Hover pencil +
   * sparkle affordances still appear. The label is still used for a11y titles.
   */
  hideLabel?: boolean;
  /** Current value: string for text/textarea/select; string[] for tags. */
  value: string | string[] | null;
  mode?: InlinePersonaMode;
  placeholder?: string;
  /** For mode="select"; ignored otherwise. First option is the implicit blank if value is null. */
  options?: InlinePersonaOption[];
  /** Caller persists the change. Called once on commit; receives the new value. */
  onSave: (next: string | string[] | null) => Promise<void> | void;
  /** Optional sparkle handler (per Q16). When omitted, the sparkle button is hidden. */
  onRegenerate?: () => void;
  /** Disable editing entirely (member viewing an owner-only agent — S23.6 Q26). */
  readOnly?: boolean;
  /** Optional render override for read-mode display. */
  renderValue?: (value: string | string[] | null) => ReactNode;
}

export function InlinePersonaEdit({
  label,
  hideLabel,
  value,
  mode = "text",
  placeholder,
  options,
  onSave,
  onRegenerate,
  readOnly,
  renderValue,
}: InlinePersonaEditProps) {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftText, setDraftText] = useState<string>(
    typeof value === "string" ? value : "",
  );
  const [draftTags, setDraftTags] = useState<string[]>(
    Array.isArray(value) ? value : [],
  );
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Sync draft if parent value changes mid-flight (e.g. a sparkle regen lands).
  useEffect(() => {
    if (editing) return;
    setDraftText(typeof value === "string" ? value : "");
    setDraftTags(Array.isArray(value) ? value : []);
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try {
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      } catch {
        /* selection not supported on this input type */
      }
    }
  }, [editing]);

  async function commitText() {
    if (saving) return;
    const next = draftText.trim().length === 0 ? null : draftText;
    const current = typeof value === "string" ? value : null;
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function commitTags(next: string[]) {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(next.length === 0 ? null : next);
    } finally {
      setSaving(false);
    }
  }

  async function commitSelect(next: string | null) {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  const showAffordances = !readOnly && (hovered || editing);

  return (
    <div
      style={wrap}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={hideLabel ? labelRowBare : labelRow}>
        {!hideLabel && <span style={labelText}>{label}</span>}
        <div style={{ flex: 1 }} />
        {showAffordances && !editing && mode !== "tags" && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={iconBtn}
            title={`Edit ${label.toLowerCase()}`}
            aria-label={`Edit ${label}`}
          >
            <i className="ti ti-pencil" style={{ fontSize: 12 }} aria-hidden="true" />
          </button>
        )}
        {showAffordances && !editing && onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            style={iconBtn}
            title={`Regenerate ${label.toLowerCase()} with AI`}
            aria-label={`Regenerate ${label}`}
          >
            <i className="ti ti-sparkles" style={{ fontSize: 12 }} aria-hidden="true" />
          </button>
        )}
      </div>

      {editing && mode === "textarea" && (
        <textarea
          ref={(el) => {
            inputRef.current = el;
          }}
          value={draftText}
          rows={3}
          disabled={saving}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditing(false);
              setDraftText(typeof value === "string" ? value : "");
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              commitText();
            }
          }}
          placeholder={placeholder}
          style={textareaStyle}
        />
      )}

      {editing && mode === "text" && (
        <input
          ref={(el) => {
            inputRef.current = el;
          }}
          type="text"
          value={draftText}
          disabled={saving}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditing(false);
              setDraftText(typeof value === "string" ? value : "");
            }
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
            }
          }}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}

      {editing && mode === "select" && (
        <select
          autoFocus
          disabled={saving}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => commitSelect(e.target.value || null)}
          onBlur={() => setEditing(false)}
          style={inputStyle}
        >
          <option value="">{placeholder ?? "Not set"}</option>
          {(options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {/* Tag editor is always-on (no pencil) — it's already inline by design. */}
      {mode === "tags" ? (
        <TagEditor
          value={draftTags}
          placeholder={placeholder}
          disabled={readOnly || saving}
          onChange={(next) => {
            setDraftTags(next);
            commitTags(next);
          }}
        />
      ) : !editing ? (
        renderValue ? (
          <div style={valueBox}>{renderValue(value)}</div>
        ) : isEmpty(value) ? (
          <div style={emptyText}>{placeholder ?? "Not set"}</div>
        ) : mode === "select" ? (
          <div style={valueBox}>
            {options?.find((o) => o.value === value)?.label ?? String(value)}
          </div>
        ) : (
          <div style={valueText}>{value as string}</div>
        )
      ) : null}
    </div>
  );
}

function isEmpty(v: string | string[] | null): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim().length === 0;
  return v.length === 0;
}

function TagEditor({
  value,
  placeholder,
  disabled,
  onChange,
}: {
  value: string[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  function add(raw: string) {
    const next = raw.trim();
    if (!next) return;
    if (value.includes(next)) {
      setDraft("");
      return;
    }
    onChange([...value, next]);
    setDraft("");
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  return (
    <div style={tagWrap}>
      {value.map((tag) => (
        <span key={tag} style={tagChip}>
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => remove(tag)}
              style={tagRemove}
              aria-label={`Remove ${tag}`}
            >
              <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden="true" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            }
            if (e.key === "Backspace" && draft.length === 0 && value.length > 0) {
              e.preventDefault();
              remove(value[value.length - 1]!);
            }
          }}
          onBlur={() => add(draft)}
          placeholder={value.length === 0 ? placeholder ?? "Add a specialty…" : "Add another…"}
          style={tagInput}
        />
      )}
      {disabled && value.length === 0 && (
        <span style={emptyText}>{placeholder ?? "Not set"}</span>
      )}
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  paddingBottom: 10,
};

const labelRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  minHeight: 18,
};

// Bare variant (hideLabel): no reserved label height — the row collapses when
// the hover affordances aren't showing, so the value sits flush.
const labelRowBare: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  minHeight: 0,
};

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const iconBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: 4,
};

const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "6px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  resize: "vertical",
  lineHeight: 1.5,
};

const valueText: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const valueBox: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
};

const emptyText: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
};

const tagWrap: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  alignItems: "center",
  minHeight: 28,
};

const tagChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 8px",
  fontSize: 12,
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  border: "var(--kuvio-border-default)",
};

const tagRemove: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: 4,
  padding: 0,
};

const tagInput: CSSProperties = {
  flex: 1,
  minWidth: 100,
  fontSize: 13,
  padding: "4px 6px",
  border: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  outline: "none",
};
